import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import type { ReactNode } from "react";

const STORAGE_KEY = "aardstats.myPlayer";

type MyPlayerContextValue = {
  // The viewer's own character, saved in this browser only.
  myPlayer: string | null;
  setMyPlayer: (name: string | null) => void;
  isMe: (name: string | null | undefined) => boolean;
};

const readStored = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const MyPlayerContext = createContext<MyPlayerContextValue>({
  myPlayer: null,
  setMyPlayer: () => undefined,
  isMe: () => false
});

export const MyPlayerProvider = ({ children }: { children: ReactNode }) => {
  const [myPlayer, setState] = useState<string | null>(readStored);

  const setMyPlayer = useCallback((name: string | null) => {
    const next = name?.trim() || null;
    setState(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage can be blocked; the choice then lasts until the page closes.
    }
  }, []);

  // Keep other open tabs in step.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setState(event.newValue || null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<MyPlayerContextValue>(() => {
    const lower = myPlayer?.toLowerCase();
    return {
      myPlayer,
      setMyPlayer,
      isMe: (name) => !!lower && name?.toLowerCase() === lower
    };
  }, [myPlayer, setMyPlayer]);

  return (
    <MyPlayerContext.Provider value={value}>
      {children}
    </MyPlayerContext.Provider>
  );
};

export const useMyPlayer = () => useContext(MyPlayerContext);
